import React from "react";
import TitleCenter from "../../../components/title/TitleCenter";
import TopBanner from "../../../components/topBanner/topBanner";
import imgBanner from "../../../images/banner/banner-tin-tuc.jpg";
import { useEffect, useRef } from "react";
import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../hooks/useLanguage";
import { getTypicalProjectsByLanguage } from "../../../apis/typicalProjects";
import { getNavMenuByLanguage } from "../../../apis/navMenus";
import { getSectionContentCardsByLanguage } from "../../../apis/sectionContentCards";
import { getTypicalProjectInformationItemsByLanguage } from "../../../apis/typicalProjectInformationItems";
import { normalizeUploadedImageUrl } from "../../../apis/uploadImage";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

const PATH_VI = "/du-an-tieu-bieu";
const PATH_OTHER = "/typical-projects";

const flattenMenus = (menus = []) =>
  menus.flatMap((menu) => [menu, ...flattenMenus(menu.children || [])]);

const getMenuSearchText = (menu) =>
  (
    menu?.title ||
    menu?.Title ||
    menu?.name ||
    menu?.Name ||
    menu?.text ||
    menu?.keyName ||
    menu?.KeyName ||
    menu?.slug ||
    menu?.Slug ||
    ""
  )
    .toString()
    .toLowerCase();

function DetailTypicalProject() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState();
  const [projectId, setProjectId] = useState(null);
  const [roleCards, setRoleCards] = useState([]);
  const [projectInfoItems, setProjectInfoItems] = useState([]);
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectInfoLoading, setProjectInfoLoading] = useState(false);
  const [activeCategory, setActiveCategori] = useState("overview");
  const abortControllerRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // Điều hướng path theo ngôn ngữ khi currentLang thay đổi
  useEffect(() => {
    const currentPath = location.pathname;
    const basePath = currentLang === "vi" ? PATH_VI : PATH_OTHER;
    const otherPath = currentLang === "vi" ? PATH_OTHER : PATH_VI;

    if (currentPath.startsWith(otherPath + "/")) {
      const slug = currentPath.slice(otherPath.length);
      navigate(`${basePath}${slug}`, { replace: true });
    }
  }, [currentLang]);

  useEffect(() => {
    // Hủy request trước nếu đang chạy
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchProject = async () => {
      setProjectLoading(true);
      try {
        const projects = await getTypicalProjectsByLanguage(currentLang);
        const found =
          projects.find((item) => item.slug === id) ??
          (projectId !== null
            ? projects.find((item) => item.id === projectId)
            : undefined);
        if (found) {
          if (found.slug !== id) {
            const basePath = currentLang === "vi" ? PATH_VI : PATH_OTHER;
            navigate(`${basePath}/${found.slug}`, { replace: true });
          }
          setData(found);
          setProjectId(found.id);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Lỗi khi tải dự án tiêu biểu:", error);
        }
      } finally {
        setProjectLoading(false);
      }
    };
    fetchProject();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id, currentLang]);

  useEffect(() => {
    let isMounted = true;

    const fetchRoleCards = async () => {
      try {
        const menus = await getNavMenuByLanguage(currentLang);
        const menuList = flattenMenus(menus);
        const typicalProjectMenu = menuList.find((menu) => {
          const slug = menu.slug || menu.Slug || "";
          const keyName = menu.keyName || menu.KeyName || "";
          const text = getMenuSearchText(menu);

          return (
            slug === "du-an-tieu-bieu" ||
            slug === "typical-projects" ||
            keyName === "menu.projects" ||
            text.includes("dự án tiêu biểu") ||
            text.includes("typical projects")
          );
        }) || { id: 6 }; // Fallback to NavMenu 6 if inactive

        if (!typicalProjectMenu?.id) {
          if (isMounted) setRoleCards([]);
          return;
        }

        const cards = await getSectionContentCardsByLanguage(
          currentLang,
          typicalProjectMenu.id,
        );
        if (isMounted) setRoleCards(cards);
      } catch (error) {
        console.error("Lỗi khi tải card Vai trò HSV:", error);
        if (isMounted) setRoleCards([]);
      }
    };

    fetchRoleCards();

    return () => {
      isMounted = false;
    };
  }, [currentLang]);

  useEffect(() => {
    let isMounted = true;

    const fetchProjectInfoItems = async () => {
      if (!projectId) {
        setProjectInfoItems([]);
        setProjectInfoLoading(false);
        return;
      }

      setProjectInfoLoading(true);
      try {
        const items = await getTypicalProjectInformationItemsByLanguage(
          currentLang,
          projectId,
        );
        if (isMounted) setProjectInfoItems(items);
      } catch (error) {
        console.error("Lỗi khi tải thông tin tổng quát dự án:", error);
        if (isMounted) setProjectInfoItems([]);
      } finally {
        if (isMounted) setProjectInfoLoading(false);
      }
    };

    fetchProjectInfoItems();

    return () => {
      isMounted = false;
    };
  }, [currentLang, projectId]);

  const formattedHTMLOverview = { __html: normalizeUploadedImageUrl(data ? data.overview : "") };
  const formattedHTMLHSVRole = { __html: normalizeUploadedImageUrl(data ? data.roleHsv : "") };
  const category = ["overview", "hsv_role"];

  return (
    <div className="DetailTypicalProject content_pages">
      <TopBanner
        titleCenter={t("home.typical_projects_title")}
        imgBanner={imgBanner}
        pageName={t("home.typical_projects_title")}
        text={data ? data.title : ""}
      />
      <div className="layout_container">
        {projectLoading ? (
          <div className="project-detail-loading-title" />
        ) : (
          <TitleCenter text={data ? data.title : ""} />
        )}
        <div className="DetailTypicalProject-content">
          <ul className="category">
            {category.map((value, index) => (
              <li
                key={index}
                className={activeCategory === value ? "category-active" : ""}
                onClick={() => setActiveCategori(value)}
              >
                {t(`typical_projects.${value}`)}
              </li>
            ))}
          </ul>
          <div className="tab-content">
            <div
              role="tabpanel"
              className={activeCategory === "overview" ? "" : "none"}
            >
              {projectLoading ? (
                <div className="project-overview-loading">
                  <div className="project-loading-card">
                    <div className="project-loading-title" />
                    <div className="project-loading-grid">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div className="project-loading-item" key={index}>
                          <span />
                          <div>
                            <i />
                            <b />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="project-loading-image" />
                </div>
              ) : (
                <div className="project-overview-image">
                  <img src={data?.image} alt={data?.title || ""} />
                </div>
              )}
              {!projectLoading && projectInfoLoading && (
                <div className="project-overview-loading">
                  <div className="project-loading-card">
                    <div className="project-loading-title" />
                    <div className="project-loading-grid">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div className="project-loading-item" key={index}>
                          <span />
                          <div>
                            <i />
                            <b />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {!projectLoading && !projectInfoLoading && projectInfoItems.length > 0 && (
                <section className="project-overview-panel">
                  <div className="project-overview-panel__backdrop">
                    <img
                      src={data?.overviewImage || data?.image || imgBanner}
                      alt=""
                    />
                  </div>
                  <div className="project-overview-card">
                    <div className="project-overview-card__heading">
                      <h2 style={{ textAlign: "center" }}>{data?.title}</h2>
                      {data?.updatedAt && (
                        <p>Cập nhật dữ liệu: {data.updatedAt}</p>
                      )}
                    </div>
                    <div className="project-info-grid">
                      {projectInfoItems.map((item) => (
                        <article className="project-info-item" key={item.id}>
                          <div className="project-info-item__icon">
                            {item.iconURL && <img src={item.iconURL} alt="" />}
                          </div>
                          <div className="project-info-item__content">
                            <h3>{item.label || item.name || item.code}</h3>
                            <p>{item.value}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {!projectLoading && !projectInfoLoading && projectInfoItems.length === 0 && (
                <>
                  {data?.overviewImage && (
                    <div
                      style={{ textAlign: "center", marginBottom: "1.6rem" }}
                    >
                      <img src={data.overviewImage} alt="" />
                    </div>
                  )}
                  <div
                    className="quill-content"
                    dangerouslySetInnerHTML={formattedHTMLOverview}
                  ></div>
                </>
              )}
            </div>
            <div
              role="tabpanel"
              className={activeCategory === "hsv_role" ? "" : "none"}
            >
              {data?.roleHsv && (
                <div
                  className="quill-content"
                  dangerouslySetInnerHTML={formattedHTMLHSVRole}
                  style={{ marginBottom: roleCards.length > 0 ? "30px" : "0" }}
                ></div>
              )}

              {roleCards.length > 0 && (
                <div className="typical-role-card-grid">
                  {roleCards.map((card) => (
                    <article
                      className={`typical-role-card ${card.isBorder ? "has-border" : ""}`}
                      key={card.id}
                      style={{
                        backgroundColor: card.color || "#fff",
                      }}
                    >
                      {card.imgURL && (
                        <div className="typical-role-card__media">
                          <img src={card.imgURL} alt={card.title || ""} />
                        </div>
                      )}
                      <div className="typical-role-card__body">
                        {card.title && (
                          <h3 style={{ color: card.textColor || "#123D2A" }}>
                            {card.title}
                          </h3>
                        )}
                        {card.subtitle && (
                          <p className="typical-role-card__subtitle">
                            {card.subtitle}
                          </p>
                        )}
                        {card.description && (
                          <div
                            className="typical-role-card__description quill-content"
                            dangerouslySetInnerHTML={{
                              __html: card.description,
                            }}
                          />
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {!data?.roleHsv && roleCards.length === 0 && (
                <div className="quill-content" style={{ color: '#888', fontStyle: 'italic' }}>
                  Đang cập nhật...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetailTypicalProject;
